export const HOST_PANEL_VIEWS = [
  'plan',
  'review',
  'files',
  'chat',
  'subagent',
  'results',
  'pull-request',
  'desktop',
  'computer',
  'cloud-browser',
  'ports',
  'new-tab',
] as const;

/** Panel ids are current host views or namespaced plugin/artifact contributions. */
export type PanelView =
  | (typeof HOST_PANEL_VIEWS)[number]
  | `plugin:${string}`
  | `artifact:${string}`;

export function isPanelView(value: string): value is PanelView {
  return (
    (HOST_PANEL_VIEWS as readonly string[]).includes(value) ||
    value.startsWith('plugin:') ||
    value.startsWith('artifact:')
  );
}

import type { CloudRuntime } from './renderer/cloud';
import type { WorkspaceClient } from './renderer/workspace';

/** Stable host services. A plugin may own hooks and state inside its panel. */
export type NativePluginContext = {
  refreshWorkspace?: (() => Promise<void>) | undefined;
  /** The conversation session a session placement (header, summary) is rendered for. */
  session?:
    | {
        executionMode: 'local' | 'worktree' | 'cloud';
        gitStatus: import('./renderer/native').NativeGitStatus | null;
        /** Structured generation with the conversation's model (commit and PR text). */
        generateText?:
          | ((prompt: string, schema: Readonly<Record<string, unknown>>) => Promise<unknown>)
          | undefined;
      }
    | undefined;
  currentBranch?: string | null | undefined;
  preview?: boolean | undefined;
  openPanel?: ((view: PanelView) => void) | undefined;
  annotateSelection?:
    | ((input: import('./renderer/selection').AnnotateSelectionInput) => void)
    | undefined;
  workspace: string | null;
  client: WorkspaceClient | null;
  cloudRuntime: CloudRuntime | null;
  taskId: string;
  visible: boolean;
  maximized: boolean;
  workspaceReady: boolean;
  services: import('./services').PluginHostServices;
  permissions: ReadonlySet<string>;
  conversation: {
    messages: readonly import('@crowdex/agent-contracts').CrowdexUIMessage[];
    busy: boolean;
    sendMessage(text: string): void;
  };
};
export type PluginComposerActionContext = {
  disabled: boolean;
  /** Sends a message to the composer's conversation, as submitting it would. */
  send?: ((text: string) => void) | undefined;
};

/** Reuses the host transcript/composer contracts while the plugin owns orchestration. */
export type PluginConversationContext = {
  conversationId: string;
  initialMessages: readonly import('@crowdex/agent-contracts').CrowdexUIMessage[];
  leadAgent: import('./renderer/native').NativeAgent | null;
  onMessagesChange?:
    | ((messages: import('@crowdex/agent-contracts').CrowdexUIMessage[]) => void)
    | undefined;
  workspaceRoot?: string | null | undefined;
  cloud?: boolean | undefined;
  composer: import('react').ReactNode;
  onRuntimeChange?: ((cloud: boolean) => Promise<void>) | undefined;
};
